import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, message, Tooltip, Spin, Empty, Badge, Card, Statistic, Row, Col, Collapse, Timeline, Typography } from 'antd';
import { 
  EyeOutlined, ReloadOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, 
  ClockCircleOutlined, RightCircleOutlined, PauseCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import { SOP_CONSTANTS } from '../constants/sopConstants';

const { Text } = Typography;
const { Panel } = Collapse;

// 步骤类型显示名称映射
const STEP_TYPE_DISPLAY = {
  'MESSAGE': '发送消息',
  'MOMENTS': '发送朋友圈',
  'TAG': '更新标签'
};

// 动作类型显示名称映射
const ACTION_TYPE_DISPLAY = {
  'SEND_MESSAGE': '发送消息',
  'SEND_MOMENT': '发送朋友圈',
  'UPDATE_TAG': '更新标签'
};

// 获取步骤类型显示名称
const getStepTypeDisplay = (stepType) => {
  return STEP_TYPE_DISPLAY[stepType] || stepType;
};

// 获取动作类型显示名称
const getActionTypeDisplay = (actionType) => {
  return ACTION_TYPE_DISPLAY[actionType] || actionType;
};

// 判断是否显示动作类型（避免重复显示）
const shouldShowActionType = (stepType, actionType) => {
  // 如果步骤类型和动作类型表示同一个操作，则不显示动作类型
  if (stepType === 'MESSAGE' && actionType === 'SEND_MESSAGE') return false;
  if (stepType === 'MOMENTS' && actionType === 'SEND_MOMENT') return false;
  if (stepType === 'TAG' && actionType === 'UPDATE_TAG') return false;
  return true;
};

const SopMemberExecutions = ({ templateId, title }) => {
  const [loading, setLoading] = useState(false);
  const [executionStats, setExecutionStats] = useState({
    totalExecutions: 0,
    completedExecutions: 0,
    inProgressExecutions: 0,
    failedExecutions: 0
  });
  const [memberExecutions, setMemberExecutions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);

  // 获取SOP执行到成员的详细记录
  const fetchMemberExecutions = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      if (!templateId) return;
      
      setLoading(true);
      
      // 获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      try {
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr);
          tenantId = tenant.id.toString();
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      // 调用API获取数据
      const response = await api.get(`/v1/sop/executions/template/${templateId}/members`, {
        params: {
          page: page - 1, // 后端分页从0开始
          size: pageSize
        },
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      
      console.log('SOP成员执行记录响应:', response.data);
      
      // 处理响应数据
      const data = response.data;
      if (!data || !data.members) {
        setMemberExecutions([]);
        setPagination({
          ...pagination,
          total: 0
        });
        return;
      }
      
      // 转换数据结构为表格可用的格式
      const membersMap = data.members;
      const formattedData = [];
      
      console.log('成员执行记录原始数据:', membersMap);
      console.log('成员分组数量:', Object.keys(membersMap).length);
      console.log('所有分组键:', Object.keys(membersMap));
      
      // 遍历每个成员及其执行记录
      Object.entries(membersMap).forEach(([memberKey, executions]) => {
        console.log(`处理分组 '${memberKey}', 包含 ${executions?.length || 0} 条执行记录`);
        
        // 处理该分组的所有执行记录，每条执行记录都应该作为独立的表格行显示
        if (executions && executions.length > 0) {
          // 排序执行记录，最新的在前
          const sortedExecutions = [...executions].sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          
          // 为每条执行记录创建表格行
          sortedExecutions.forEach((execution, index) => {
            console.log(`成员 ${memberKey} 的第${index + 1}条执行记录:`, execution);
            console.log(`该执行记录的状态:`, execution.status);
            
            if (execution.steps && execution.steps.length > 0) {
              console.log(`该执行记录包含 ${execution.steps.length} 个步骤:`);
              execution.steps.forEach((step, stepIndex) => {
                console.log(`步骤 ${stepIndex + 1} - ${step.stepName} - 状态: ${step.status}`);
              });
              
              // 统计各类状态的步骤数量
              const statusCount = execution.steps.reduce((acc, step) => {
                acc[step.status] = (acc[step.status] || 0) + 1;
                return acc;
              }, {});
              console.log(`步骤状态统计:`, statusCount);
            } else {
              console.log(`该执行记录不包含步骤信息`);
            }
            
            // 创建表格行数据
            const calculateExecutionStatus = (execution) => {
              // 如果本身已标记为失败，直接返回
              if (execution.status && execution.status.toUpperCase() === 'FAILED') {
                return 'FAILED';
              }
              
              // 检查是否有失败的步骤
              if (execution.steps && execution.steps.length > 0) {
                const hasFailedSteps = execution.steps.some(step => 
                  step.status && step.status.toUpperCase() === 'FAILED'
                );
                
                if (hasFailedSteps) {
                  console.log('检测到失败的步骤，将执行记录状态更新为失败');
                  return 'FAILED';
                }
              }
              
              // 保持原有状态
              return execution.status;
            };

            // 为当前执行记录创建唯一的行ID，避免重复
            const uniqueRowId = `${execution.id}_${index}`;

            formattedData.push({
              id: uniqueRowId, // 使用唯一ID避免React key重复
              executionId: execution.id, // 保留原始执行记录ID
              memberName: memberKey, // 使用成员名称作为显示
              targetType: execution.targetType,
              targetId: execution.targetId,
              targetName: execution.targetName,
              triggerOperatorId: execution.triggerOperatorId,
              triggerOperatorName: execution.triggerOperatorName,
              status: calculateExecutionStatus(execution), // 重新计算状态
              currentDay: execution.currentDay || 0,
              completedSteps: execution.completedSteps || 0,
              totalSteps: execution.totalSteps || 0,
              startTime: execution.startTime,
              lastStepDate: execution.lastStepDate,
              // 添加原始执行记录，便于查看详情
              executionDetails: execution,
              // 步骤信息
              steps: execution.steps || []
            });
          });
        }
      });
      
      setMemberExecutions(formattedData);
      setPagination({
        ...pagination,
        current: page,
        total: data.totalMembers || formattedData.length
      });
      
      // 更新统计数据
      updateExecutionStats(formattedData);
      
      // 更新活动的折叠面板
      updateActiveCollapseKeys(formattedData);
    } catch (error) {
      console.error('获取SOP成员执行记录失败:', error);
      message.error('获取SOP成员执行记录失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 更新活动的折叠面板状态
  const updateActiveCollapseKeys = (executionRecords) => {
    // 收集所有失败步骤的ID
    const failedStepIds = [];
    executionRecords.forEach(record => {
      const steps = record.steps || [];
      steps.forEach(step => {
        if (step.status && step.status.toUpperCase() === 'FAILED') {
          failedStepIds.push(step.id);
        }
      });
    });
    setActiveCollapseKeys(failedStepIds);
  };

  // 从执行记录中统计数据
  const updateExecutionStats = (executionRecords) => {
    if (!executionRecords || executionRecords.length === 0) {
      setExecutionStats({
        totalExecutions: 0,
        completedExecutions: 0,
        inProgressExecutions: 0,
        failedExecutions: 0
      });
      return;
    }
    
    let completedCount = 0;
    let inProgressCount = 0;
    let failedCount = 0;
    
    executionRecords.forEach(record => {
      if (record.status === 'COMPLETED') {
        completedCount++;
      } else if (record.status === 'IN_PROGRESS' || record.status === 'PENDING') {
        inProgressCount++;
      } else if (record.status === 'FAILED') {
        failedCount++;
      }
    });
    
    setExecutionStats({
      totalExecutions: executionRecords.length,
      completedExecutions: completedCount,
      inProgressExecutions: inProgressCount,
      failedExecutions: failedCount
    });
  };

  useEffect(() => {
    if (templateId) {
      fetchMemberExecutions();
    }
  }, [templateId]);

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    fetchMemberExecutions(pagination.current, pagination.pageSize);
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchMemberExecutions();
  };

  // 显示执行状态标签
  const renderStatus = (status) => {
    let color = 'default';
    let text = '未知';
    
    // 记录原始状态值，帮助调试
    console.log('渲染状态值:', status);
    
    // 规范化状态字符串，处理可能的大小写和格式差异
    const normalizedStatus = String(status || '').toLowerCase().trim();
    console.log('规范化后的状态值:', normalizedStatus);
    
    switch (normalizedStatus) {
      case 'in_progress':
      case 'inprogress':
      case 'in-progress':
      case 'running':
        color = 'processing';
        text = '进行中';
        break;
      case 'completed':
      case 'complete':
      case 'done':
      case 'finished':
        color = 'success';
        text = '已完成';
        break;
      case 'failed':
      case 'failure':
      case 'fail':
      case 'error':
        color = 'error';
        text = '失败';
        break;
      case 'cancelled':
      case 'canceled':
      case 'cancel':
        color = 'warning';
        text = '已取消';
        break;
      case 'pending':
      case 'wait':
      case 'waiting':
        color = 'default';
        text = '等待中';
        break;
      default:
        console.warn(`未识别的状态值: ${status}`);
        break;
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  // 显示步骤状态标签
  const renderStepStatus = (status) => {
    let color = 'default';
    let text = '未知';
    
    // 记录原始步骤状态值，帮助调试
    console.log('渲染步骤状态值:', status);
    
    // 规范化状态字符串
    const normalizedStatus = String(status || '').toLowerCase().trim();
    console.log('规范化后的步骤状态值:', normalizedStatus);
    
    switch (normalizedStatus) {
      case 'in_progress':
      case 'inprogress':
        color = 'processing';
        text = '执行中';
        break;
      case 'completed':
      case 'complete':
        color = 'success';
        text = '已完成';
        break;
      case 'failed':
      case 'failure':
      case 'fail':
      case 'error':
        color = 'error';
        text = '失败';
        break;
      case 'skipped':
        color = 'default';
        text = '已跳过';
        break;
      case 'pending':
        color = 'blue';
        text = '待执行';
        break;
      default:
        console.warn(`未识别的步骤状态值: "${status}"`);
        break;
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // 获取步骤状态图标
  const getStepStatusIcon = (status) => {
    if (!status) return <RightCircleOutlined />;
    
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'FAILED':
        return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      case 'IN_PROGRESS':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'SKIPPED':
        return <PauseCircleOutlined style={{ color: '#d9d9d9' }} />;
      case 'PENDING':
        return <RightCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <RightCircleOutlined />;
    }
  };

  // 获取步骤状态颜色
  const getStepStatusColor = (status) => {
    if (!status) return 'blue';
    
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return 'green';
      case 'FAILED':
        return 'red';
      case 'IN_PROGRESS':
        return 'blue';
      case 'SKIPPED':
        return 'gray';
      case 'PENDING':
        return 'blue';
      default:
        return 'blue';
    }
  };

  // 渲染步骤执行记录
  const renderStepExecutions = (record) => {
    console.log('正在渲染步骤执行记录，记录数据:', record);
    const steps = record.steps || [];
    console.log('从记录中获取的步骤数据:', steps);
    
    if (steps.length === 0) {
      console.log('没有步骤数据，显示空提示');
      return <Empty description="暂无步骤执行记录" />;
    }
    
    // 按步骤顺序排序
    const sortedSteps = [...steps].sort((a, b) => {
      if (a.executeTime && b.executeTime) {
        return new Date(a.executeTime) - new Date(b.executeTime);
      }
      if (!a.executeTime) return 1;
      if (!b.executeTime) return -1;
      return a.stepOrder - b.stepOrder;
    });
    
    console.log('排序后的步骤数据:', sortedSteps);
    
    // 查找失败的步骤ID
    const failedStepIds = sortedSteps
      .filter(step => step.status && step.status.toUpperCase() === 'FAILED')
      .map(step => step.id);
      
    console.log('失败的步骤IDs:', failedStepIds);
    
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          {failedStepIds.length > 0 && (
            <div style={{ color: '#ff4d4f', marginTop: 8 }}>
              <strong>⚠️ 存在 {failedStepIds.length} 个失败的步骤</strong>
            </div>
          )}
        </div>
        <Timeline mode="left">
          {sortedSteps.map(step => {
            const isFailed = step.status && step.status.toUpperCase() === 'FAILED';
            return (
              <Timeline.Item
                key={step.id}
                dot={getStepStatusIcon(step.status)}
                color={getStepStatusColor(step.status)}
              >
                <Collapse 
                  ghost 
                  activeKey={activeCollapseKeys}
                  onChange={setActiveCollapseKeys}
                >
                  <Panel 
                    header={
                      <Space>
                        <span style={isFailed ? { color: '#ff4d4f', fontWeight: 'bold' } : {}}>
                          {step.stepName}
                        </span>
                        {renderStepStatus(step.status)}
                      </Space>
                    } 
                    key={step.id}
                    style={isFailed ? { borderLeft: '3px solid #ff4d4f', paddingLeft: 8 } : {}}
                  >
                    <div style={{ paddingLeft: 8 }}>
                      {/* <p><Text strong>步骤类型:</Text> {getStepTypeDisplay(step.stepType)}</p> */}
                      {/* {shouldShowActionType(step.stepType, step.actionType) && ( */}
                        <p><Text strong>动作类型:</Text> {getActionTypeDisplay(step.actionType)}</p>
                      {/* )} */}
                      <p><Text strong>计划执行时间:</Text> {formatDate(step.executeTime)}</p>
                      <p><Text strong>开始执行时间:</Text> {formatDate(step.startTime)}</p>
                      <p><Text strong>完成时间:</Text> {formatDate(step.completionTime)}</p>
                      
                      {step.status === 'COMPLETED' && step.result && (
                        <p>
                          <Text strong>执行结果:</Text> 
                          <div style={{ wordBreak: 'break-all' }}>{step.result}</div>
                        </p>
                      )}
                      
                      {isFailed && step.errorMessage && (
                        <p>
                          <Text strong type="danger">错误信息:</Text> 
                          <div style={{ 
                            background: '#fff2f0', 
                            border: '1px solid #ffccc7',
                            padding: '8px',
                            marginTop: '8px',
                            borderRadius: '4px' 
                          }}>
                            <Text type="danger">{step.errorMessage}</Text>
                          </div>
                        </p>
                      )}
                      
                      {step.status === 'FAILED' && step.retryCount > 0 && (
                        <p>
                          <Text strong>重试次数:</Text> 
                          {step.retryCount}/{step.maxRetries || '-'}
                        </p>
                      )}
                    </div>
                  </Panel>
                </Collapse>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </div>
    );
  };

  // 处理行展开
  const handleRowExpand = (expanded, record) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };

  // 表格列定义
  const columns = [
    {
      title: '目标',
      dataIndex: 'targetName',
      key: 'targetName',
      render: (text, record) => (
        <div>
          <div>{text || record.targetName || '未知'}</div>
          <div><small>类型: {record.targetType === 'CUSTOMER' ? '客户' : '客户群'}</small></div>
        </div>
      )
    },
    {
      title: '触发人',
      dataIndex: 'triggerOperatorName',
      key: 'triggerOperatorName',
      width: 120,
      render: (name, record) => {
        if (name) {
          return name;
        }
        // 如果没有姓名，显示触发人ID
        if (record.triggerOperatorId) {
          return record.triggerOperatorId;
        }
        return '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatus
    },
    {
      title: '当前天数',
      dataIndex: 'currentDay',
      key: 'currentDay',
      width: 100,
      render: (text) => text || 0
    },
    {
      title: '进度',
      key: 'progress',
      width: 120,
      render: (_, record) => {
        const currentStep = record.completedSteps || 0;
        const totalSteps = record.totalSteps || 0;
        return `${currentStep}/${totalSteps}`;
      }
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: (text) => formatDate(text)
    },
    {
      title: '最后步骤日期',
      dataIndex: 'lastStepDate',
      key: 'lastStepDate',
      width: 180,
      render: (text) => formatDate(text)
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title || 'SOP执行情况'}</span>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={handleRefresh}
        >
          刷新
        </Button>
      </div>
      
      {/* 执行统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总执行次数" 
              value={executionStats.totalExecutions} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="进行中" 
              value={executionStats.inProgressExecutions} 
              valueStyle={{ color: '#1890ff' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已完成" 
              value={executionStats.completedExecutions} 
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="执行失败" 
              value={executionStats.failedExecutions} 
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>
      
      <Spin spinning={loading}>
        {memberExecutions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={memberExecutions}
            rowKey="id"
            pagination={pagination}
            onChange={handleTableChange}
            expandable={{
              expandedRowKeys: expandedRowKeys,
              onExpand: handleRowExpand,
              expandedRowRender: renderStepExecutions
            }}
          />
        ) : (
          <Empty description="暂无执行记录" />
        )}
      </Spin>
    </div>
  );
};

export default SopMemberExecutions;